import { Injectable } from '@angular/core';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { YeehroCalendlyBookingService } from './yeehro-calendly-booking.service';
import { YeehroCalendlyAvailabilityService } from './yeehro-calendly-availability.service';

// Shared "offer time slots" logic — loads a host's available slots, filters them
// to a range, default-selects the first few per day, and renders the selection
// as rich HTML + plain text for embedding in an email. Extracted from
// offer-time-slots-dialog (which copies the same output to the clipboard) so the
// Share Availability compose form can reuse it without duplicating the date /
// timezone / slot-URL plumbing.

export interface ShareSlotItem {
  time: string;       // raw "9:00am"
  startLabel: string; // display start
  endLabel: string;   // display end (start + duration)
  url: string;        // deep link to the public reservation page for this slot
}

export interface ShareDaySlots {
  dateLabel: string;             // "Monday, June 16"
  dateStr: string;               // "2026-06-16"
  visibleSlots: ShareSlotItem[]; // the slots that will render in the email
}

export interface ShareRangeOption { key: string; label: string; }

@Injectable({ providedIn: 'root' })
export class YeehroSlotShareService {
  // Matches the URL the offer-time-slots-dialog emits so pasted/embedded links
  // stay consistent across both entry points.
  private readonly bookingBaseUrl = 'https://yeehro.com/booking/';
  private readonly DEFAULT_SLOTS_PER_DAY = 6;

  readonly rangeOptions: ShareRangeOption[] = [
    { key: 'next-3', label: 'Next 3 available days' },
    { key: 'next-5', label: 'Next 5 available days' },
    { key: 'this-week', label: 'This week' },
    { key: 'next-week', label: 'Next week' }
  ];

  constructor(
    private _bookingService: YeehroCalendlyBookingService,
    private _availabilityService: YeehroCalendlyAvailabilityService
  ) {}


  // Loads slots for the event across the current + next month, narrows to the
  // requested range, and default-selects the first N per day. Returns the day
  // view models plus the resolved timezone label.
  async loadGroupedSlots(event: any, bookingUsername: string, range: string): Promise<{ days: ShareDaySlots[]; timezoneLabel: string; }> {
    if (!event?._id || !bookingUsername) return { days: [], timezoneLabel: '' };

    let eventTimezone = event?.schedule?.timezone || 'America/New_York';
    const now = new Date();
    const monthsToFetch = this.getMonthKeysToFetch(now);

    // Pass the sender as host so a public template resolves to THEIR schedule
    // (matching the per-slot URLs, which use bookingUsername). The event must be
    // active — inactive meeting types aren't bookable, so we don't share them.
    const responses = await Promise.all(
      monthsToFetch.map((m: string) => lastValueFrom(this._bookingService.getAvailableSlots(event._id, m, bookingUsername)))
    );

    const dayMap = new Map<string, Array<{ time: string }>>();
    for (const resp of responses) {
      if (resp?.timezone) eventTimezone = resp.timezone;
      for (const day of (resp?.days || [])) {
        if (!dayMap.has(day?.date)) dayMap.set(day.date, day?.slots || []);
      }
    }

    const rawDays = Array.from(dayMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, slots]) => ({ date, slots }));

    const filtered = this.applyRange(rawDays, range, eventTimezone);
    const duration = Number(event?.duration) || 30;

    const days: ShareDaySlots[] = filtered.map(day => ({
      dateLabel: this.formatDayLabel(day.date),
      dateStr: day.date,
      visibleSlots: (day.slots || [])
        .slice(0, this.DEFAULT_SLOTS_PER_DAY)
        .map(s => this.buildSlotItem(event, bookingUsername, day.date, s.time, duration))
    })).filter(d => d.visibleSlots.length);

    return { days, timezoneLabel: this.formatTimezoneLabel(eventTimezone) };
  }


  // Rich HTML for pasting into Gmail / Outlook / any rich-text email. Inline
  // styles only (clients strip <style>); inline-block pills (no flex). Mirrors
  // the offer-time-slots-dialog output so both surfaces look identical.
  buildHtml(event: any, days: ShareDaySlots[], bookingUsername: string, timezoneLabel: string): string {
    if (!days?.length) return '';

    const safeName = this.escapeHtml(event?.name || '');
    const duration = Number(event?.duration) || 0;
    const safeDuration = duration ? `${duration} min` : '';
    const safeTz = this.escapeHtml(timezoneLabel);
    const landing = `${this.bookingBaseUrl}${bookingUsername}/${event?.bookingLinkId || event?._id}`;
    const safeLanding = this.escapeHtml(landing);

    const chipStyle = [
      'display:inline-block', 'padding:7px 14px', 'margin:0 6px 6px 0',
      'border:1px solid #b8c4d6', 'border-radius:4px', 'background:#ffffff',
      'color:#004eba', 'font-size:13px', 'font-weight:500',
      'text-decoration:none', 'line-height:1', 'vertical-align:middle'
    ].join(';');

    const sections = days.map(day => {
      const safeDate = this.escapeHtml(day.dateLabel);
      const chips = day.visibleSlots.map(slot => {
        const label = this.escapeHtml(slot.startLabel);
        const href = this.escapeHtml(slot.url);
        return `<a href="${href}" style="${chipStyle}">${label}</a>`;
      }).join('');
      return `<div style="margin-top:18px;">`
        + `<div style="font-weight:700; color:#1a1a2e; font-size:14px; margin-bottom:10px;">${safeDate}</div>`
        + `<div>${chips}</div>`
        + `</div>`;
    }).join('');

    const headerLines: string[] = [];
    if (safeName) headerLines.push(`<div style="font-weight:700; font-size:16px; color:#1a1a2e;">${safeName}</div>`);
    if (safeDuration) headerLines.push(`<div style="color:#4a5261; margin-top:2px;">${safeDuration}</div>`);
    if (safeTz) headerLines.push(`<div style="color:#4a5261; margin-top:2px;">Time zone: ${safeTz}</div>`);

    return `<div style="font-family: Arial, Helvetica, sans-serif; color:#1a1a2e; font-size:14px; line-height:1.4;">`
      + headerLines.join('')
      + sections
      + `<div style="margin-top:20px;"><a href="${safeLanding}" style="color:#004eba; text-decoration:underline; font-size:13px;">See all available times</a></div>`
      + `</div>`;
  }


  // ── Internals (ported from offer-time-slots-dialog) ───────


  private applyRange(rawDays: Array<{ date: string; slots: Array<{ time: string }> }>, range: string, tz: string) {
    const today = this.dateStringInTz(new Date(), tz);
    if (range === 'next-3' || range === 'next-5') {
      const count = range === 'next-3' ? 3 : 5;
      return rawDays.filter(d => d.date >= today).slice(0, count);
    }
    if (range === 'this-week') {
      const endOfWeek = this.endOfWeek(today);
      return rawDays.filter(d => d.date >= today && d.date <= endOfWeek);
    }
    if (range === 'next-week') {
      const nextMon = this.addDays(this.endOfWeek(today), 1);
      const nextSun = this.addDays(nextMon, 6);
      return rawDays.filter(d => d.date >= nextMon && d.date <= nextSun);
    }
    return rawDays.filter(d => d.date >= today).slice(0, 3);
  }


  private buildSlotItem(event: any, bookingUsername: string, dateStr: string, time: string, duration: number): ShareSlotItem {
    const slug = event?.bookingLinkId || event?._id;
    const hhmm = this.timeStrToHHmm(time);
    return {
      time,
      startLabel: time || '',
      endLabel: this.addMinutesToTimeStr(time, duration),
      url: `${this.bookingBaseUrl}${bookingUsername}/${slug}/${dateStr}-${hhmm}`
    };
  }


  private dateStringInTz(d: Date, tz: string): string {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    } catch (err) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  }


  private endOfWeek(dateStr: string): string {
    const d = this.parseDateStr(dateStr);
    const dow = d.getDay();
    const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
    return this.dateStringFromDate(this.addDaysToDate(d, daysUntilSunday));
  }


  private addDays(dateStr: string, days: number): string {
    return this.dateStringFromDate(this.addDaysToDate(this.parseDateStr(dateStr), days));
  }


  private addDaysToDate(d: Date, days: number): Date {
    const copy = new Date(d.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
  }


  private parseDateStr(dateStr: string): Date {
    const [y, m, dd] = (dateStr || '').split('-').map(Number);
    return new Date(y || 1970, (m || 1) - 1, dd || 1);
  }


  private dateStringFromDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }


  private getMonthKeysToFetch(now: Date): string[] {
    const monthOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return [monthOf(now), monthOf(next)];
  }


  private formatDayLabel(dateStr: string): string {
    const d = this.parseDateStr(dateStr);
    try {
      return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(d);
    } catch (err) {
      return dateStr;
    }
  }


  private timeStrToHHmm(time: string): string {
    if (!time) return '';
    const m = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!m) {
      const m24 = time.match(/^(\d{1,2}):(\d{2})$/);
      if (m24) return `${String(parseInt(m24[1])).padStart(2, '0')}${m24[2]}`;
      return '';
    }
    let h = parseInt(m[1]);
    const min = m[2];
    const ampm = m[3].toLowerCase();
    if (ampm === 'pm' && h !== 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}${min}`;
  }


  private addMinutesToTimeStr(time: string, mins: number): string {
    if (!time) return '';
    const m = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!m) return time;
    let h = parseInt(m[1]);
    const sm = parseInt(m[2]);
    const ampm = m[3].toLowerCase();
    if (ampm === 'pm' && h !== 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    const total = h * 60 + sm + (mins || 0);
    const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
    const eh24 = Math.floor(wrapped / 60);
    const em = wrapped % 60;
    const eampm = eh24 >= 12 ? 'pm' : 'am';
    const eh12 = eh24 % 12 || 12;
    return `${eh12}:${String(em).padStart(2, '0')}${eampm}`;
  }


  private formatTimezoneLabel(tz: string): string {
    if (!tz) return '';
    return this._availabilityService?.getTimezoneLabel(tz) || tz.replace(/_/g, ' ');
  }


  private escapeHtml(value: string): string {
    if (!value) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
